/**
 * First-success + usefulness eligibility (localStorage, fail-closed).
 * Care Dock remains in care-dock-storage; this module orchestrates priority.
 */

import {
  FIRST_SUCCESS_DONE_KEY,
  FIRST_SUCCESS_TOOL_KEY,
  USEFULNESS_COOLDOWN_UNTIL_KEY,
  USEFULNESS_DISMISS_COOLDOWN_MS,
  USEFULNESS_LAST_SHOWN_AT_KEY,
  USEFULNESS_RESHOW_AFTER_MS,
  USEFULNESS_SUCCESS_COUNT_KEY,
  USEFULNESS_SUCCESS_THRESHOLD,
  USEFULNESS_VOTE_COOLDOWN_MS,
  type CareSurface,
  type CareTool,
} from "@/lib/care-prompt-keys";
import { recordCareDockSuccess } from "@/lib/care-dock-storage";

function safeGetItem(key: string): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): boolean {
  try {
    /* v8 ignore next -- residual branch: callers pre-check availability */
    if (typeof localStorage === "undefined") return false;
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function isCarePromptStorageAvailable(): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    const probe = "__vortixy_care_prompt_probe__";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function isFirstSuccessDone(): boolean {
  return safeGetItem(FIRST_SUCCESS_DONE_KEY) === "1";
}

/**
 * One-time claim for the first successful Vortixy Chat session.
 * Marks done immediately so it never reappears.
 */
export function tryClaimFirstSuccess(tool: CareTool): boolean {
  if (!isCarePromptStorageAvailable()) return false;
  if (isFirstSuccessDone()) return false;
  if (tool !== "chat") return false;
  if (!safeSetItem(FIRST_SUCCESS_DONE_KEY, "1")) return false;
  safeSetItem(FIRST_SUCCESS_TOOL_KEY, tool);
  return true;
}

/** Explicit complete (CTA / dismiss) — idempotent; already set on claim. */
export function completeFirstSuccess(): void {
  if (!isCarePromptStorageAvailable()) return;
  safeSetItem(FIRST_SUCCESS_DONE_KEY, "1");
}

function parseNonNegInt(raw: string | null): number {
  if (raw == null || raw === "") return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function parseTimestamp(raw: string | null): number | null {
  if (raw == null || raw === "") return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export type UsefulnessSnapshot = {
  successCount: number;
  lastShownAt: number | null;
  cooldownUntil: number | null;
};

export function readUsefulnessSnapshot(): UsefulnessSnapshot {
  return {
    successCount: parseNonNegInt(safeGetItem(USEFULNESS_SUCCESS_COUNT_KEY)),
    lastShownAt: parseTimestamp(safeGetItem(USEFULNESS_LAST_SHOWN_AT_KEY)),
    cooldownUntil: parseTimestamp(safeGetItem(USEFULNESS_COOLDOWN_UNTIL_KEY)),
  };
}

export function isInUsefulnessCooldown(now: number = Date.now()): boolean {
  const { cooldownUntil } = readUsefulnessSnapshot();
  return cooldownUntil != null && now < cooldownUntil;
}

export function shouldShowUsefulness(
  snapshot: UsefulnessSnapshot,
  now: number = Date.now()
): boolean {
  if (snapshot.cooldownUntil != null && now < snapshot.cooldownUntil) return false;
  if (snapshot.successCount >= USEFULNESS_SUCCESS_THRESHOLD) return true;
  if (
    snapshot.lastShownAt != null &&
    now - snapshot.lastShownAt >= USEFULNESS_RESHOW_AFTER_MS &&
    snapshot.successCount >= 1
  ) {
    return true;
  }
  return false;
}

export function recordUsefulnessSuccess(now: number = Date.now()): { shouldShow: boolean } {
  if (!isCarePromptStorageAvailable()) return { shouldShow: false };
  if (isInUsefulnessCooldown(now)) return { shouldShow: false };

  const prev = readUsefulnessSnapshot();
  const successCount = prev.successCount + 1;
  if (!safeSetItem(USEFULNESS_SUCCESS_COUNT_KEY, String(successCount))) {
    return { shouldShow: false };
  }
  const next = { ...prev, successCount };
  if (!shouldShowUsefulness(next, now)) return { shouldShow: false };

  safeSetItem(USEFULNESS_SUCCESS_COUNT_KEY, "0");
  safeSetItem(USEFULNESS_LAST_SHOWN_AT_KEY, String(now));
  return { shouldShow: true };
}

export function dismissUsefulness(now: number = Date.now()): void {
  if (!isCarePromptStorageAvailable()) return;
  safeSetItem(USEFULNESS_COOLDOWN_UNTIL_KEY, String(now + USEFULNESS_DISMISS_COOLDOWN_MS));
  safeSetItem(USEFULNESS_SUCCESS_COUNT_KEY, "0");
}

export function markUsefulnessVote(now: number = Date.now()): void {
  if (!isCarePromptStorageAvailable()) return;
  safeSetItem(USEFULNESS_COOLDOWN_UNTIL_KEY, String(now + USEFULNESS_VOTE_COOLDOWN_MS));
  safeSetItem(USEFULNESS_SUCCESS_COUNT_KEY, "0");
}

/**
 * Priority queue after a real tool success:
 * first-success → usefulness (while not in usefulness cooldown) → care-dock.
 * Only one surface per success; care-dock does not count while usefulness is accumulating.
 */
export function resolveCareSurfaceAfterSuccess(
  tool: CareTool,
  now: number = Date.now()
): CareSurface | null {
  if (tryClaimFirstSuccess(tool)) {
    return "first-success";
  }

  if (isFirstSuccessDone()) {
    if (!isInUsefulnessCooldown(now)) {
      const useful = recordUsefulnessSuccess(now);
      if (useful.shouldShow) return "usefulness";
      // Still building toward usefulness — do not also advance Care Dock this run.
      return null;
    }
    const dock = recordCareDockSuccess(now);
    if (dock.shouldShow) return "care-dock";
    return null;
  }

  // A user who has not completed a chat session only sees the Care Dock.
  return null;
}
